import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  NgClass,
  NgFor,
  NgIf,
  NgSwitch,
  NgSwitchCase,
  NgTemplateOutlet,
} from '@angular/common';
import { map, Observable, skip, Subject, switchMap, takeUntil } from 'rxjs';

import { ComparisonService } from '../../../services/comparison.service';
import { LayersMenuService } from '../../../../menu/right-menu/layers-menu/layers-menu.service';
import { UserAccessService } from '../../../../../user/access/user-access.service';
import { formatLayerName } from '../../../../common-colorscale/layer-name-format.pipe';
import { formatNumbers } from '../../../../../shared/pipes/number-formatter.pipe';
import { SubscriptionPlans } from '../../../../../user/user/user.model';
import {
  COUNTY_LEVEL_LAYER,
  H3_RES5_LEVEL_LAYER,
  H3_RES7_LEVEL_LAYER,
} from '../../../services/layer-store.service';
import {
  LAND_AREA_FRACTION,
  POPULATION,
} from '../../../../../shared/types/feature-data-type';
import {
  COUNTY_ONLY_FEATURES,
  COUNTY_ONLY_UNAVAILABLE_NOTE,
  GLANCE_LABELS,
  GLANCE_TILES,
  HERO_SCORE,
  HERO_SUB_INDICES,
  OVERVIEW_TAB_ID,
  POPUP_TABS,
  PopupIcon,
  PopupTab,
} from '../popup-groups';
import {
  hasNationalComparison,
  NationalDeviation,
  nationalDeviation,
  nationalValueOf,
} from '../national-comparison';
import { hwwGrade, HwwGrade } from '../hww-grade';

/**
 * Why a row's number is hidden and what the visitor can do about it. Two
 * different gates sit in front of the map data and they ask for very
 * different things, so a guest is told which rows a free account opens and
 * which ones are actually being sold. See UserAccessService.isBySubscription.
 */
export interface LockCopy {
  /** The free-with-an-account gate. Painted brand blue, not paywall violet. */
  free: boolean;
  /** Caption under the row, in place of the US comparison. */
  note: string;
  /** The word on the row's own button. */
  cta: string;
  /** Full phrase for screen readers, prefixed to the metric's name. */
  aria: string;
}

const ACCOUNT_LOCK: LockCopy = {
  free: true,
  note: 'Free with an account',
  cta: 'Sign up',
  aria: 'Sign up free to see',
};

const PLAN_LOCK: LockCopy = {
  free: false,
  note: 'Included in a paid plan',
  cta: 'Unlock',
  aria: 'Unlock',
};

export interface MetricRow {
  feature: string;
  label: string;
  value: string;
  /** Null when the visitor may see the number. */
  lock: LockCopy | null;
  deviation: NationalDeviation | null;
  /** `US avg 78,538` — the reference the bar is drawn against. */
  reference: string;
  /** Long-form comparison, shown on hover and read by screen readers. */
  note: string;
  /**
   * The metric is one the map can be painted with, so the row is a control:
   * clicking it recolours the map the way the right menu's radio would. False
   * for a locked row (the padlock owns that click) and for anything the menu
   * has no map layer for.
   */
  colorable: boolean;
  /** Already the layer on the map — the row says so instead of offering it. */
  onMap: boolean;
  /**
   * Set only on the HWW indices, which are graded rather than compared: the
   * row draws the score block's meter in place of the US bar. Null everywhere
   * else, and on a locked row.
   */
  grade: HwwGrade | null;
}

export interface GlanceTile extends MetricRow {
  icon: PopupIcon;
  caption: string;
}

export interface SubIndexRow {
  label: string;
  grade: HwwGrade | null;
  locked: boolean;
}

export interface RenderSection {
  label: string | null;
  note: string | null;
  rows: MetricRow[];
}

export interface RenderTab {
  id: string;
  label: string;
  icon: PopupIcon;
  sections: RenderSection[];
  /**
   * The padlock on the tab itself, or null when nothing inside is hidden.
   * A tab holding even one paywalled row wears the paid padlock: it is the
   * stronger claim, and signing up would not empty the tab.
   */
  lock: LockCopy | null;
}

/** Short names for the hero meters. The full names live in the tooltips. */
const SUB_INDEX_LABELS = ['Health', 'Income', 'Education'];

/** How many metrics the overview calls out as unusual for this place. */
const STANDOUT_COUNT = 4;

/**
 * The body of the location popup: a national grade, then four subject tabs.
 *
 * Replaces the flat list this popup used to show (see LevelsPopupListComponent,
 * still used for the hover popup, where one value is the whole point). Four
 * things changed and each is load-bearing:
 *
 *  - Every row is drawn against the national value the ComparisonService
 *    already caches, so the comparison stops being a sentence at the end of a
 *    line and becomes the row's shape.
 *  - Subjects are tabs rather than one long scroll, so the card opens at a
 *    readable height on a phone and each panel is a short, complete list.
 *  - The overview names the handful of metrics furthest from the US average,
 *    which is the analysis a reader would otherwise have to do by scrolling.
 *  - Features the visitor's plan does not cover are *rendered locked* rather
 *    than filtered out. Hiding them meant the strongest upgrade argument this
 *    product has — the data sitting one click away — was invisible.
 */
@Component({
  selector: 'app-grouped-popup-list',
  templateUrl: './grouped-popup-list.component.html',
  styleUrls: ['./grouped-popup-list.component.scss'],
  imports: [NgIf, NgFor, NgClass, NgSwitch, NgSwitchCase, NgTemplateOutlet],
})
export class GroupedPopupListComponent implements OnChanges, OnDestroy {
  @Input() properties!: { [key: string]: any };
  @Input() title!: string;
  @Input() activeLayer!: string;
  @Input() activeLevel!: string;
  /** Drawn instead of `N/A` while the colourscale for `activeLayer` loads. */
  @Input() isLoading = false;

  /**
   * A locked metric the visitor asked to see. The shell owns the upsell modal
   * and the analytics that go with it: this component only knows what was
   * clicked, which keeps it free of the modal/GA dependency graph.
   */
  @Output() unlockRequested = new EventEmitter<{
    feature: string;
    label: string;
  }>();

  /**
   * A readable metric was clicked and should become the map's colour. The
   * shell owns the LayerStore write for the same reason it owns the upsell
   * modal: this component reports what was clicked and nothing else.
   */
  @Output() colorByRequested = new EventEmitter<{
    feature: string;
    label: string;
  }>();

  public heroTitle = '';
  public heroGrade: HwwGrade | null = null;
  public heroLock: LockCopy | null = null;
  public subIndices: SubIndexRow[] = [];

  public activeRow: MetricRow | null = null;
  public glance: GlanceTile[] = [];
  public standouts: MetricRow[] = [];
  public tabs: RenderTab[] = [];

  public activeTabId = OVERVIEW_TAB_ID;

  private comparisonNotes: { [feature: string]: string } = {};
  private lookup = new Map<string, any>();
  private mapColorCache: Set<string> | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly properties$ = new Subject<{ [key: string]: any }>();

  constructor(
    private comparisonService: ComparisonService,
    private accessService: UserAccessService,
    private layersMenuService: LayersMenuService
  ) {
    // `switchMap`, because the shell fills `properties` in more than once per
    // popup (MapPopupComponent's written-in-state and area-score loads both
    // mutate it). Re-subscribing per change left every one of those requests in
    // flight, each having read the same empty localStorage and each issuing the
    // same fetch; only the last answer is wanted, and cancelling the rest is
    // what stops the duplicates.
    this.properties$
      .pipe(
        switchMap((properties) => this.loadComparison(properties)),
        takeUntil(this.destroy$)
      )
      .subscribe((notes) => {
        this.comparisonNotes = notes;
        // National values only reach localStorage here, so the deviation bars
        // are drawn on this second pass rather than the first render.
        this.build();
      });

    // What the visitor may see is read once, at build time, so signing in
    // while this popup is open otherwise left every padlock - the overall
    // grade included - exactly as it was until the page was reloaded. The
    // numbers themselves are already in `properties` (the features endpoint
    // answers anonymous callers too), so re-running the build is the whole
    // fix. `skip(1)` drops the BehaviorSubject's current value: ngOnChanges
    // builds the first render.
    this.accessService.accessStatus
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(() => this.build());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['properties']) {
      this.indexProperties();
      this.properties$.next(this.properties);
    }

    this.build();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public selectTab(id: string): void {
    this.activeTabId = id;
  }

  public get activeTab(): RenderTab | null {
    return this.tabs.find((tab) => tab.id === this.activeTabId) ?? null;
  }

  public get isOverview(): boolean {
    return this.activeTabId === OVERVIEW_TAB_ID;
  }

  /** A locked row is the whole point of showing it, so the row is the trigger. */
  public handleLockedClick(row: MetricRow): void {
    if (!row.lock) return;
    this.unlockRequested.emit({ feature: row.feature, label: row.label });
  }

  /** Paints the map with this metric, exactly as the right menu's radio does. */
  public handleColorByClick(row: MetricRow): void {
    if (!row.colorable || row.onMap) return;
    this.colorByRequested.emit({ feature: row.feature, label: row.label });
  }

  public handleHeroLockedClick(): void {
    this.unlockRequested.emit({ feature: HERO_SCORE, label: this.heroTitle });
  }

  /** Water cells carry the administrative area's data, which skews everything. */
  public get isMostlyWater(): boolean {
    const fraction = this.lookup.get(LAND_AREA_FRACTION);
    return (
      typeof fraction === 'number' &&
      fraction < 0.75 &&
      (this.activeLevel === H3_RES5_LEVEL_LAYER ||
        this.activeLevel === H3_RES7_LEVEL_LAYER)
    );
  }

  /**
   * Set when the map is coloured by a county-only layer at a finer level and
   * has nothing to show for it, so the empty pinned row says why instead of
   * leaving a bare `N/A`.
   *
   * Gated on the row actually being empty: a value and "published only at the
   * county level" side by side would contradict each other.
   */
  public get activeLayerLevelNote(): string | null {
    if (this.activeRow?.value !== 'N/A') return null;

    const isCountyOnly = COUNTY_ONLY_FEATURES.some(
      (feature) => feature.toUpperCase() === this.activeLayer?.toUpperCase()
    );

    return isCountyOnly && this.activeLevel !== COUNTY_LEVEL_LAYER
      ? COUNTY_ONLY_UNAVAILABLE_NOTE
      : null;
  }

  public get isTinyPopulation(): boolean {
    const population = this.lookup.get(POPULATION.toUpperCase());
    return typeof population === 'number' && population < 10;
  }

  /**
   * Bar geometry. The track is the full row and the centre tick is the
   * national value, so a fill of `n%` reaches `n/50` of the way to saturation
   * on whichever side of the country the cell sits.
   *
   * The 2% floor keeps a real-but-small gap visible: without it every metric
   * within a few percent of the country renders as an empty track, which reads
   * as missing data rather than as a close match.
   */
  public fillWidth(deviation: NationalDeviation): number {
    if (deviation.offset === 0) return 0;
    return Math.max(Math.abs(deviation.offset) * 50, 2);
  }

  public fillOffset(deviation: NationalDeviation): number {
    return deviation.offset >= 0 ? 50 : 50 - this.fillWidth(deviation);
  }

  public trackByFeature(_index: number, row: MetricRow): string {
    return row.feature;
  }

  public trackById(_index: number, tab: RenderTab): string {
    return tab.id;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  private indexProperties(): void {
    this.lookup.clear();
    for (const key in this.properties ?? {}) {
      // Tile server properties arrive lowercase, backend ones uppercase, and
      // the same metric can come from either depending on the zoom level.
      this.lookup.set(key.toUpperCase(), this.properties[key]);
    }
  }

  /** Comparison notes keyed the way `buildRow` looks them up. */
  private loadComparison(properties: {
    [key: string]: any;
  }): Observable<{ [feature: string]: string }> {
    return this.comparisonService
      .getAndCalculateComparisonData(properties)
      .pipe(
        map((notes) => {
          const byFeature: { [feature: string]: string } = {};
          for (const key in notes ?? {}) {
            if (notes![key]) byFeature[key.toUpperCase()] = notes![key];
          }
          return byFeature;
        })
      );
  }

  private build(): void {
    const plan = this.accessService.getUserPlan();

    this.buildHero(plan);

    this.activeRow = this.activeLayer
      ? this.buildRow(this.activeLayer, plan, true)
      : null;

    this.tabs = POPUP_TABS.map((tab) => this.buildTab(tab, plan)).filter(
      (tab) => tab.sections.some((section) => section.rows.length > 0)
    );

    this.glance = GLANCE_TILES.map(({ feature, icon }) => {
      const row = this.buildRow(feature, plan, false);
      return row
        ? { ...row, icon, caption: GLANCE_LABELS[feature] ?? row.label }
        : null;
    }).filter((tile): tile is GlanceTile => tile !== null);

    this.standouts = this.pickStandouts();
  }

  /**
   * The metrics furthest from the national value, largest gap first.
   *
   * Ranked on the bar's own `offset` rather than the raw percentage, so a
   * ratio metric at 3x the country and a trend 2 points above it can be
   * compared on one scale. Whatever the map is already coloured by is left
   * out, being pinned directly above.
   *
   * Locked metrics are ranked alongside the visible ones and rendered
   * redacted, the same way they are inside the tabs. They used to be dropped
   * here, which quietly made the heading a lie for a guest: the four rows on
   * screen were the furthest *of the ones they were allowed to see*, and the
   * genuine outlier one padlock away was never mentioned.
   */
  private pickStandouts(): MetricRow[] {
    const seen = new Set<string>([this.activeLayer?.toUpperCase()]);
    const excluded = new Set(
      POPUP_TABS.flatMap((tab) =>
        tab.sections
          .filter((section) => section.excludeFromHighlights)
          .flatMap((section) => section.features.map((f) => f.toUpperCase()))
      )
    );
    const candidates: { row: MetricRow; gap: number }[] = [];

    for (const tab of this.tabs) {
      for (const section of tab.sections) {
        for (const row of section.rows) {
          const key = row.feature.toUpperCase();
          if (seen.has(key) || excluded.has(key)) continue;
          // A locked row carries no deviation of its own - drawing one would
          // hand over the number the padlock is hiding - so the gap it is
          // ranked on is measured here and goes no further than this sort.
          const deviation = row.deviation ?? this.deviationOf(row.feature);
          if (!deviation) continue;
          seen.add(key);
          candidates.push({ row, gap: Math.abs(deviation.offset) });
        }
      }
    }

    return candidates
      .sort((a, b) => b.gap - a.gap)
      .slice(0, STANDOUT_COUNT)
      .map(({ row }) => row);
  }

  /**
   * How far `feature` sits from the US, whether or not the visitor may see
   * it. The single definition of the gap: `buildRow` draws it, `pickStandouts`
   * ranks on it, and a locked row uses it without exposing it.
   */
  private deviationOf(feature: string): NationalDeviation | null {
    const raw = this.lookup.get(feature.toUpperCase());
    if (typeof raw !== 'number') return null;

    return nationalDeviation(
      feature,
      raw,
      hasNationalComparison(feature) ? nationalValueOf(feature) : null
    );
  }

  /**
   * Every metric the right menu can paint the map with, upper-cased.
   *
   * Read from the menu rather than restated here: it is the only place that
   * knows which metrics have a map layer behind them, and a row offering to
   * recolour the map with something that has none would simply blank it. The
   * menu's group data is built once at construction, so this is cached.
   */
  private get mapColorFeatures(): Set<string> {
    if (!this.mapColorCache) {
      this.mapColorCache = new Set(
        this.layersMenuService.groupsData
          .flatMap((group) => group.data.mapColor ?? [])
          .map((block) => block.featureConst.toUpperCase())
      );
    }

    return this.mapColorCache;
  }

  /**
   * Whether the map has a layer for `feature`.
   *
   * The HWW indices are the one family the menu registers unscoped
   * (`WEALTH_INDEX`) while the map is painted with the scoped name
   * (`WEALTH_INDEX_by_country`), which is also what this popup reads. Both
   * spellings mean the same layer, so the base name is what gets looked up.
   */
  private isColorable(feature: string): boolean {
    const key = feature.toUpperCase();
    return (
      this.mapColorFeatures.has(key) ||
      this.mapColorFeatures.has(key.split('_BY_')[0])
    );
  }

  /**
   * Which gate `feature` sits behind, or null when the visitor may see it.
   * Only a guest can meet the account gate: for anyone signed in without a
   * plan the trial list is exactly what they already have, so every padlock
   * they see is the paid one.
   */
  private lockFor(feature: string): LockCopy | null {
    if (this.accessService.userHasAccess(feature)) return null;
    return this.accessService.isBySubscription(feature)
      ? PLAN_LOCK
      : ACCOUNT_LOCK;
  }

  private buildHero(plan: SubscriptionPlans | null): void {
    // "Neighborhood Score by Country" — the scope is spelled out in the
    // caption underneath, so it would only be said twice here.
    this.heroTitle = formatLayerName(HERO_SCORE, plan)
      .replace(/\s+by\s+Country$/i, '')
      .trim();

    this.heroLock = this.lockFor(HERO_SCORE);
    this.heroGrade = this.heroLock
      ? null
      : hwwGrade(HERO_SCORE, this.lookup.get(HERO_SCORE.toUpperCase()));

    this.subIndices = HERO_SUB_INDICES.map((feature, index) => {
      const locked = !!this.lockFor(feature);
      return {
        label: SUB_INDEX_LABELS[index],
        locked,
        grade: locked
          ? null
          : hwwGrade(feature, this.lookup.get(feature.toUpperCase())),
      };
    });
  }

  private buildTab(tab: PopupTab, plan: SubscriptionPlans | null): RenderTab {
    const sections = tab.sections
      .filter(
        (section) =>
          !section.levels || section.levels.includes(this.activeLevel)
      )
      .map((section) => ({
        label: section.label,
        note: section.note ?? null,
        rows: section.features
          .map((feature) => this.buildRow(feature, plan, false))
          .filter((row): row is MetricRow => row !== null),
      }))
      .filter((section) => section.rows.length > 0);

    const locks = sections.flatMap((section) =>
      section.rows
        .map((row) => row.lock)
        .filter((lock): lock is LockCopy => lock !== null)
    );

    return {
      id: tab.id,
      label: tab.label,
      icon: tab.icon,
      sections,
      lock: locks.find((lock) => !lock.free) ?? locks[0] ?? null,
    };
  }

  /**
   * @param keepEmpty the active layer keeps its row even with nothing in it,
   *   because "N/A here" is the answer to the question the visitor just asked
   *   by clicking the cell. Everywhere else an empty row is noise.
   */
  private buildRow(
    feature: string,
    plan: SubscriptionPlans | null,
    keepEmpty: boolean
  ): MetricRow | null {
    // The HWW indices come back named for their scope ("Income Index by
    // Country"). Every number in this card is read against the country, the
    // score block above says so once, and the row has no width to spare.
    const name = formatLayerName(feature, plan).replace(
      /\s+by\s+country$/i,
      ''
    );
    if (!name) return null;

    const lock = this.lockFor(feature);
    const raw = this.lookup.get(feature.toUpperCase());
    const { label, prefix, suffix } = splitUnit(name);

    const onMap =
      !!this.activeLayer &&
      feature.toUpperCase() === this.activeLayer.toUpperCase();

    if (lock) {
      return {
        feature,
        label,
        value: '',
        lock,
        deviation: null,
        reference: '',
        note: '',
        // The padlock owns the click on a locked row; painting the map with a
        // number the visitor cannot read would be the worst of both.
        colorable: false,
        onMap,
        grade: null,
      };
    }

    const isEmpty = raw == null || raw === '';
    if (isEmpty && !keepEmpty) return null;

    const formatted = isEmpty ? '' : formatNumbers(raw, 2, feature, true);
    if (formatted === '' && !keepEmpty) return null;

    // An extensive quantity or one whose country figure is not the same
    // measure has no honest reference, so neither the caption nor the bar is
    // drawn — see NO_COMPARISON_EXACT in national-comparison.ts.
    const national = hasNationalComparison(feature)
      ? nationalValueOf(feature)
      : null;
    // The reference goes through the *same* formatter as the cell's own value
    // above. Anything else and the two numbers in the row are not comparable —
    // an earlier pre-scaling step here turned a national obesity rate of 0.32
    // into "3200%", because formatNumbers already multiplies these fractions
    // by 100 on its own.
    const reference =
      national == null
        ? ''
        : `US avg ${prefix}${formatNumbers(national, 2, feature, true)}${suffix}`;

    return {
      feature,
      label,
      value: formatted === '' ? 'N/A' : `${prefix}${formatted}${suffix}`,
      lock: null,
      deviation: this.deviationOf(feature),
      reference,
      note: this.comparisonNotes[feature.toUpperCase()] ?? reference,
      colorable: this.isColorable(feature),
      onMap,
      // Same call the score block makes, so the letter and the fill in a tab
      // cannot disagree with the ones on the overview.
      grade: hwwGrade(feature, raw),
    };
  }
}

/**
 * `formatLayerName` returns display names with the unit tacked on after a
 * comma ("Median household income, $"). In a two-column row the unit belongs
 * with the number, not the label — but only the two units that read as an
 * affix. "offenses per 100K ppl" is a phrase and stays where it is.
 */
function splitUnit(name: string): {
  label: string;
  prefix: string;
  suffix: string;
} {
  if (name.endsWith(', $')) {
    return { label: name.slice(0, -3), prefix: '$', suffix: '' };
  }
  if (name.endsWith(', %')) {
    return { label: name.slice(0, -3), prefix: '', suffix: '%' };
  }
  return { label: name, prefix: '', suffix: '' };
}
